import * as React from 'react';
import { useReducer, useEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { PagedResult, Result } from '../../services/ServiceBase';
import Loader from '../shared/Loader';
import { toast } from 'react-toastify';


type GenericSearchResultsState<T> = {
    isLoading: boolean;
    isNoResults: boolean;
    canLoadMore: boolean;
    items: T[];
    totalItems: number;
    limitExceeded: boolean;
}

export default function GenericSearchResults<T, F>(props: {
    listItemsPaged: (offset: number, filter: F) => Promise<Result<PagedResult<T>>>;
    renderBefore?: (totalItems: number) => React.ReactElement;
    renderItemValues: (item: T) => React.ReactElement;
    filter: F;
    //columnsCount: number;
    useWindowScroll?: boolean; // used by infinite scroll, defaults to TRUE. set to FALSE if component is used in a modal
    hideIfNoResults?: boolean;
    onTotalItemsChanged?: (count: number) => void;
    onIsNoResultsChanged?: (isNoResults: boolean) => void;
    maximumToLoad?: number;
}) {

    let [state, setState] = useReducer(
        (prevState: GenericSearchResultsState<T>, newState: Partial<GenericSearchResultsState<T>>) => ({ ...prevState, ...newState }),
        {
            isLoading: false,
            isNoResults: false,
            canLoadMore: false,
            items: [],
            totalItems: 0,
            limitExceeded: false
        }
    );

    const mountRef = useRef(true);

    const search = () => {

        setState({
            canLoadMore: false,
            isLoading: true,
            isNoResults: false,
            items: [],
            totalItems: 0,
            limitExceeded: false
        });


        try {
            props.listItemsPaged(0, props.filter).then((result) => {
                if (!result) {
                    return;
                }

                if (result.statusCode === 499) {
                    return;
                }

                if (!mountRef.current)
                    return;

                if (result.hasErrors) {

                    toast.error("Error Loading Data");

                    setState({
                        isLoading: false
                    });
                } else {

                    let limitExceededSearch = props.maximumToLoad > 0
                        ? result.value.Items.length >= props.maximumToLoad
                        : false;

                    setState({
                        isLoading: false,
                        items: result.value.Items,
                        isNoResults: result.value.Items.length == 0,
                        canLoadMore: (result.value.TotalItems > result.value.Items.length && limitExceededSearch == false) || result.value.Items.length == 0,
                        totalItems: result.value.TotalItems,
                        limitExceeded: limitExceededSearch
                    });
                }
            });
        }
        catch{
        }
    }

    const loadMore = (pageIndex: number) => {

        setState({
            canLoadMore: false,
            isLoading: true
        });

        if (props.listItemsPaged) {
            props.listItemsPaged(state.items.length, props.filter).then((result) => {
                if (!result) {
                    return;
                }

                if (result.statusCode === 499) {
                    return;
                }

                if (!mountRef.current)
                    return;

                if (result.hasErrors) {

                    toast.error("Error Loading Data");                    

                    setState({
                        isLoading: false
                    });
                } else {

                    let updatedItems = state.items.concat(result.value.Items);

                    let limitExceededLoad = props.maximumToLoad > 0
                        ? updatedItems.length >= props.maximumToLoad
                        : false;

                    setState({
                        isLoading: false,
                        items: updatedItems,
                        //isNoResults: result.value.Items.length == 0,
                        canLoadMore: (result.value.TotalItems > updatedItems.length && limitExceededLoad == false)
                            && (result.value.Items.length > 0),
                        totalItems: result.value.TotalItems,
                        limitExceeded: limitExceededLoad
                    });
                }
            });
        }
    }

    useEffect(() => {

        search();

    }, [props.filter]);

    useEffect(() => {

        return () => {
            mountRef.current = false;
        }

    }, []);

    useEffect(() => {

        if (props.onTotalItemsChanged)
            props.onTotalItemsChanged(state.totalItems);

    }, [state.totalItems])

    useEffect(() => {

        if (props.onIsNoResultsChanged)
            props.onIsNoResultsChanged(state.isNoResults);

    }, [state.isNoResults])


    if (props.hideIfNoResults == true && state.isNoResults)
        return null;

    return (<>
        {props.renderBefore && props.renderBefore(state.totalItems)}
        <div className={props.useWindowScroll === true ? 'scrollable-listing' : ''}>
            <InfiniteScroll
                pageStart={0}
                loadMore={loadMore}
                hasMore={state.canLoadMore && !state.isLoading}
                useWindow={props.useWindowScroll === true || props.useWindowScroll === undefined}
            >
                {state.items.map((item: T) => props.renderItemValues(item))}
                {state.isNoResults &&
                    <div className="no-results my-4">No Results</div>
                }
                {state.isLoading && <Loader />}
            </InfiniteScroll>
        </div>
        </>
    );
};